import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { PrivateAxios } from '../../../environment/AxiosInstance';
import './FlowReport.css';

const FlowReportIndex = () => {
    const [fms, setFms] = useState([]);
    const [fmsId, setFmsId] = useState(null);
    const [isDarkTheme, setIsDarkTheme] = useState(false);

    // Handle styling for Theme
    useEffect(() => {
        const checkTheme = () => setIsDarkTheme(document.body.classList.contains("dark-theme"));
        checkTheme();
        const observer = new MutationObserver(checkTheme);
        observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
        return () => observer.disconnect();
    }, []);

    // Initial Data Fetch
    useEffect(() => {
        GetFms();
        GetFmsEntity();
    }, []);

    // ---------------------------------------------------------
    // STATUS REPORT STATE & LOGIC
    // ---------------------------------------------------------

    // Dropdown Data
    const [statusFields, setStatusFields] = useState([]);
    const [statusValues, setStatusValues] = useState([]);

    // Selected Values
    const [selectedStatusField, setSelectedStatusField] = useState(null);
    const [selectedStatusValue, setSelectedStatusValue] = useState(null);

    // Report Data
    const [statusReportData, setStatusReportData] = useState([]);
    const [statusLoading, setStatusLoading] = useState(false);
    const [reportHeaders, setReportHeaders] = useState([]);

    // 1. Fetch Initial Fields when FMS Changes
    useEffect(() => {
        if (fmsId) {
            // Reset downstream selections
            setStatusFields([]);
            setStatusValues([]);
            setSelectedStatusField(null);
            setSelectedStatusValue(null);
            setStatusReportData([]);
            setReportHeaders([]);

            PrivateAxios.post('report/status/fields', { fmsId })
                .then(res => {
                    if (res.data.status) {
                        setStatusFields(res.data.data);
                    }
                })
                .catch(err => console.error("Error fetching status fields", err));
        }
    }, [fmsId]);

    // 2. Fetch Values when Field is Selected
    const handleStatusFieldChange = (field) => {
        setSelectedStatusField(field);
        setSelectedStatusValue(null);
        setStatusValues([]);
        setStatusReportData([]);
        setReportHeaders([]);

        if (field) {
            PrivateAxios.post('report/status/values', { fmsId, fieldId: field.id })
                .then(res => {
                    if (res.data.status) {
                        // Res data is array of strings
                        const options = res.data.data.map(val => ({ label: val, value: val }));
                        setStatusValues(options);
                    }
                })
                .catch(err => console.error("Error fetching status values", err));
        }
    };

    // 3. Fetch Report Data when Value is Selected
    const handleStatusValueChange = (valOption) => {
        setSelectedStatusValue(valOption);
        setStatusReportData([]);
        setReportHeaders([]);

        if (valOption) {
            setStatusLoading(true);
            PrivateAxios.post('report/status/data', {
                fmsId,
                fieldId: selectedStatusField.id,
                fieldValue: valOption.value
            })
                .then(res => {
                    if (res.data.status) {
                        setReportHeaders(res.data.headers || []);
                        setStatusReportData(res.data.data);
                    }
                    setStatusLoading(false);
                })
                .catch(err => {
                    console.error("Error fetching status report", err);
                    setStatusLoading(false);
                });
        }
    };
    // ---------------------------------------------------------

    const exportToExcel = () => {
        if (statusReportData.length === 0) return;

        // Construct Data for Excel
        const excelData = statusReportData.map(row => {
            const rowData = {};

            // 1. Dynamic Fields
            if (reportHeaders.length > 0) {
                reportHeaders.forEach(header => {
                    rowData[header] = row.formValues?.[header] || '-';
                });
            } else {
                rowData[`Identifier (${selectedStatusField?.label})`] = row.identifier;
            }

            // 2. Static Fields
            rowData['Process ID'] = row.taskId;
            rowData['Start Date'] = row.startDate ? new Date(row.startDate).toLocaleDateString() : '-';

            // Status Text
            let statusText = 'Unknown';
            if (row.status == 1) statusText = 'Todo';
            else if (row.status == 2) statusText = 'In Progress';
            else if (row.status == 3) statusText = 'Skipped';
            else if (row.status == 4) statusText = 'Completed';
            rowData['Status'] = statusText;

            rowData['Pending Step'] = row.pendingStep;
            rowData['Planned Date'] = row.stepPlanDate ? new Date(row.stepPlanDate).toLocaleDateString() : '-';
            rowData['Delay'] = row.delayHours > 0 ? `${row.delayHours} hrs` : 'On Time';

            return rowData;
        });

        // Generate Worksheet
        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Workflow Report");

        // Download File
        XLSX.writeFile(wb, `Workflow_Status_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const GetFms = () => {
        PrivateAxios.get('report/all-fms')
            .then((res) => {
                setFms(res.data.data)
            }).catch((err) => {
            })
    }

    const GetFmsEntity = () => {
        PrivateAxios.get('report/get-fms-entity')
            .then((res) => {
                if (res.data.data?.work_flow_id) {
                    setFmsId(res.data.data.work_flow_id);
                }
            }).catch((err) => {
            })
    }

    const GetFmsFrom = (selectedOption) => {
        if (!selectedOption) return;
        setFmsId(selectedOption.id);
    }

    return (
        <React.Fragment>
            <div className="p-4 flow-report-container" >
                <div className="mb-4 d-flex align-items-center justify-content-betweem">
                    <Link className="text-dark back-btn me-3" to="/reports">
                        <i className="fas fa-long-arrow-alt-left me-1" />Back
                    </Link>
                </div>

                <div className="container-fluid px-0">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                            <h5 className="card-title mb-0">Workflow Status Report</h5>
                            {statusReportData.length > 0 && (
                                <button className="btn btn-success btn-sm" onClick={exportToExcel}>
                                    <i className="fas fa-file-excel me-2"></i>Export to Excel
                                </button>
                            )}
                        </div>
                        <div className='card-body p-3'>

                            {/* ================= STATUS REPORT VIEW ================= */}
                            <div className="status-report-container">
                                <div className="row mb-4">
                                    <div className="col-md-4">
                                        <label className="form-label">Workflow/s</label>
                                        <Select
                                            placeholder="Select Workflow..."
                                            options={fms}
                                            getOptionLabel={(option) => option.name}
                                            getOptionValue={(option) => option.id}
                                            value={fms.length > 0 && fmsId ? fms.find(item => item.id == fmsId) : null}
                                            onChange={GetFmsFrom}
                                            classNamePrefix="react-select"
                                            styles={{
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: isDarkTheme ? "#06162f" : "#ffffff",
                                                }),
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Select Initial Field</label>
                                        <Select
                                            placeholder="Select Field..."
                                            options={statusFields}
                                            getOptionLabel={(opt) => opt.label}
                                            getOptionValue={(opt) => opt.id}
                                            value={selectedStatusField}
                                            onChange={handleStatusFieldChange}
                                            isDisabled={!fmsId}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Select Value</label>
                                        <Select
                                            placeholder="Select Value..."
                                            options={statusValues}
                                            value={selectedStatusValue}
                                            onChange={handleStatusValueChange}
                                            isDisabled={!selectedStatusField}
                                        />
                                    </div>
                                </div>

                                {statusLoading && <div className="text-center py-4"><div className="spinner-border text-primary" role="status"></div></div>}

                                {!statusLoading && statusReportData.length > 0 && (
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-striped table-hover">
                                            <thead className="table-light">
                                                <tr>
                                                    {/* Dynamic Headers */}
                                                    {reportHeaders.length > 0 ? (
                                                        reportHeaders.map((header, i) => (
                                                            <th key={i}>{header}</th>
                                                        ))
                                                    ) : (
                                                        <th>Identifier ({selectedStatusField?.label})</th>
                                                    )}
                                                    <th>Process ID</th>
                                                    <th>Start Date</th>
                                                    <th>Status</th>
                                                    <th>Pending Step</th>
                                                    <th>Planned Date</th>
                                                    <th>Delay (Hrs)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {statusReportData.map((row, idx) => (
                                                    <tr key={idx}>
                                                        {/* Dynamic Data */}
                                                        {reportHeaders.length > 0 ? (
                                                            reportHeaders.map((header, i) => (
                                                                <td key={i}><span className="fw-bold">{row.formValues?.[header] || '-'}</span></td>
                                                            ))
                                                        ) : (
                                                            <td><span className="fw-bold">{row.identifier}</span></td>
                                                        )}
                                                        <td>{row.taskId}</td>
                                                        <td>{row.startDate ? new Date(row.startDate).toLocaleDateString() : '-'}</td>
                                                        <td>
                                                            {row.status == 1 && <span className="badge bg-secondary">Todo</span>}
                                                            {row.status == 2 && <span className="badge bg-primary">In Progress</span>}
                                                            {row.status == 3 && <span className="badge bg-warning text-dark">Skipped</span>}
                                                            {row.status == 4 && <span className="badge bg-success">Completed</span>}
                                                        </td>
                                                        <td>{row.pendingStep}</td>
                                                        <td>{row.stepPlanDate ? new Date(row.stepPlanDate).toLocaleDateString() : '-'}</td>
                                                        <td>
                                                            {row.delayHours > 0 ? (
                                                                <span className="text-danger fw-bold">{row.delayHours} hrs</span>
                                                            ) : (
                                                                <span className="text-success">On Time</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {!statusLoading && selectedStatusValue && statusReportData.length === 0 && (
                                    <div className="alert alert-info">No matching processes found.</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default FlowReportIndex;